import type {
  ActiveStatus,
  BaseUserDTO,
  ExecutionErrCode,
  ExecutionStatus,
  WorkflowDTO,
  WorkflowType,
} from '../workflow.types';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { TenantSimpleModel } from '@repositories/tenantRepository/models/TenantSimpleModel';
import type { TenantSimple } from '@repositories/tenantRepository/tenant.types';
import { WorkflowActionDTOModel } from './WorkflowActionDTOModel';
import { WorkflowRespModel } from './WorkflowRespModel';
import { WorkflowTriggerDTOModel } from './WorkflowTriggerDTOModel';

export class WorkflowModel {
  private readonly _dto: WorkflowDTO;
  private readonly _creator: BaseUserDTO;
  private readonly _actions: WorkflowActionDTOModel[];
  private readonly _latestExecutionTime: Dayjs | null;
  private readonly _latestErrorCode: ExecutionErrCode | null;
  private readonly _remoteWorkflow: WorkflowRespModel;
  private readonly _tenant: TenantSimpleModel;
  private readonly _trigger: WorkflowTriggerDTOModel;
  private readonly _createdDate: Dayjs;

  constructor(dto: WorkflowDTO) {
    this._dto = dto;
    this._creator = dto.creator || { id: '', name: '-', username: '' };
    this._actions = dto.actions?.length ? dto.actions.map(action => new WorkflowActionDTOModel(action)) : [];
    this._latestExecutionTime = dto.latestExecutionTime ? dayjs(dto.latestExecutionTime) : null;
    this._remoteWorkflow = new WorkflowRespModel(dto.remoteWorkflow || {});
    this._tenant = new TenantSimpleModel(dto.tenant ?? ({} as TenantSimple));
    this._trigger = new WorkflowTriggerDTOModel(dto.trigger ?? {});
    this._createdDate = dayjs(dto.createdDate);
    this._latestErrorCode = null;
  }

  get originDTO(): WorkflowDTO {
    return this._dto;
  }

  get errorCode(): ExecutionErrCode | null {
    return this._latestErrorCode;
  }

  get creator(): BaseUserDTO {
    return this._creator;
  }

  get actions(): WorkflowActionDTOModel[] {
    return this._actions;
  }

  get description(): string {
    return this._dto.description ?? '';
  }

  get id(): string {
    return this._dto.id ?? '';
  }

  get latestExecutionStatus(): ExecutionStatus | undefined {
    return this._dto.latestExecutionStatus;
  }

  get latestExecutionStatusMessage(): string {
    return this._dto.latestExecutionStatusMessage ?? '';
  }

  get latestExecutionTime(): Dayjs | null {
    return this._latestExecutionTime;
  }

  get name(): string {
    return this._dto.name ?? '';
  }

  get remoteId(): string {
    return this._dto.remoteId ?? '';
  }

  get remoteWorkflow(): WorkflowRespModel {
    return this._remoteWorkflow;
  }

  get status(): ActiveStatus {
    return this._dto.status ?? 'INACTIVE';
  }

  get tenant(): TenantSimpleModel {
    return this._tenant;
  }

  get trigger(): WorkflowTriggerDTOModel {
    return this._trigger;
  }

  get type(): WorkflowType {
    return this._dto.type ?? 'CUSTOM';
  }

  get createdBy(): string {
    return this._dto.createdBy ?? '';
  }

  get createdDate(): Dayjs {
    return this._createdDate;
  }

  get isLatestExecutionSuccess(): boolean {
    switch (this._dto.latestExecutionStatus) {
      case 'COMPLETED':
      case 'READY':
      case 'RUNNING':
        return true;
      case 'FAILED':
      case 'STOPPED':
      case 'STOP_REQUESTED':
      default:
        return false;
    }
  }

  get latestExecutionStatusStr(): string {
    // NOTE. 한번도 실행되지 않는 경우 빈 문자열을 넘김
    if (!this.latestExecutionTime) return '';
    return this.isLatestExecutionSuccess ? i18next.t('Workflow_Main_12') : i18next.t('Workflow_Main_13');
  }
}
